import React, { useEffect } from "react";
import { Box, Typography, Grid, useTheme, useMediaQuery } from "@mui/material";
import Button from "../../common/Button";
import { acknowledgeNotification, deleteNotification, deleteNotificationRequest, notificationAttachmentDTO, NotificationListDTO } from "../../../redux/slice/inbox/createNotificationSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { fetchOrderDocumentsByIds, SearchOrder } from "../../../redux/slice/order/OrderSlice";
import { useNavigation } from "../../../hooks/useNavigation";
import { SearchIVRListRequest } from "../../../redux/slice/ivr/createIVRSlice";
import { SearchPracticeRequest } from "../../../redux/slice/practiceSlice";
import CustomIconButton from "../../common/CustomIconButton";
import { PreviewOutlined } from "@mui/icons-material";
import { setSingleAlertObj, useCommonReducer } from "../../../redux/slice/commonSlice";
import PDFViewer from "../../common/pdf/PDFViewer";
import OptionsPopup, { OptionPopupObjectProps } from "../../common/OptionsPopup";
import AssignPopup from "../inbox/AssignPopup";
import { SearchInvoiceInfo } from "../../../redux/slice/invoice/InvoiceSlice";
import { SearchPaymentRequest } from "../../../redux/slice/payment/paymentSlice";
import { useModalStyles } from "../../common/useModalStyles";

interface NotificationItemProps {
  notification: NotificationListDTO;
  time: string;
  formattedTime?: string;
  formattedDate?: string;
  acknowledged: boolean;
  isAssignable: boolean;
  index: number;
  total: number;
  onAcknowledge: () => void;
  showGoButton?: boolean;
  createdDate?: string;
  createdTime?: string;
  ackedByUserName?: string;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  time,
  formattedTime,
  formattedDate,
  acknowledged,
  isAssignable,
  index,
  total,
  onAcknowledge,
  showGoButton,
  createdDate,
  createdTime,
  ackedByUserName,
}) => {
  const theme = useTheme();
  const { tokens: mTokens, isDark } = useModalStyles();
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [showAssignPopup, setShowAssignPopup] = React.useState<boolean>(false);
  const [pdfContent, setPDFContent] = React.useState<string>("");
  const [showPDF, setShowPDF] = React.useState<{ isShow: boolean, title: string, fileName: string }>({ isShow: false, title: "", fileName: "" });
  const [showOptionPopup, setShowOptionsPopup] = React.useState<OptionPopupObjectProps>({ message: '', show: false });
  const { documentDataFromCommonApi } = useCommonReducer();
  const [popupMode, setPopupMode] = React.useState<string>('');

  const isLast = index === total - 1;

  const actionBtnBase = {
    textTransform: "none" as const,
    fontWeight: 700,
    fontSize: "0.78rem",
    borderRadius: "8px",
    py: 0.55,
    px: 1.15,
    minWidth: "unset",
    whiteSpace: "nowrap" as const,
    lineHeight: 1.2,
    "& .MuiTypography-root": { color: "inherit", fontWeight: 700, fontSize: "inherit" },
  };

  const ackBtnSx = isDark ? {
    ...actionBtnBase,
    color: "#fff",
    borderColor: "rgba(255,255,255,0.55)",
    backgroundColor: "rgba(255,255,255,0.04)",
    "& .MuiTypography-root": { color: "#fff" },
    "&:hover": {
      color: "#0f172a",
      borderColor: "#fff",
      backgroundColor: "#fff",
      boxShadow: "0 4px 14px rgba(255,255,255,0.22)",
      "& .MuiTypography-root": { color: "#0f172a" },
    },
  } : {
    ...actionBtnBase,
    color: "#14B8A6",
    borderColor: "rgba(20,184,166,0.55)",
    backgroundColor: "rgba(20,184,166,0.04)",
    "&:hover": {
      color: "#fff",
      borderColor: "#14B8A6",
      backgroundColor: "#14B8A6",
      boxShadow: "0 4px 14px rgba(20,184,166,0.28)",
      "& .MuiTypography-root": { color: "#fff" },
    },
  };

  const viewBtnSx = {
    ...actionBtnBase,
    color: "#00B8D9",
    borderColor: "rgba(0,184,217,0.55)",
    backgroundColor: isDark ? "rgba(0,184,217,0.08)" : "rgba(0,184,217,0.04)",
    "&:hover": {
      color: "#fff",
      borderColor: "#00B8D9",
      backgroundColor: "#00B8D9",
      boxShadow: "0 4px 14px rgba(0,184,217,0.28)",
      "& .MuiTypography-root": { color: "#fff" },
    },
  };

  const goAssignBtnSx = {
    ...actionBtnBase,
    color: "#14B8A6",
    borderColor: "rgba(20,184,166,0.55)",
    backgroundColor: isDark ? "rgba(20,184,166,0.08)" : "rgba(20,184,166,0.04)",
    "&:hover": {
      color: "#fff",
      borderColor: "#14B8A6",
      backgroundColor: "#14B8A6",
      boxShadow: "0 4px 14px rgba(20,184,166,0.28)",
      "& .MuiTypography-root": { color: "#fff" },
    },
  };

  const deleteBtnSx = {
    ...actionBtnBase,
    color: mTokens.error,
    borderColor: "rgba(239,68,68,0.55)",
    backgroundColor: isDark ? "rgba(239,68,68,0.08)" : "rgba(239,68,68,0.04)",
    "&:hover": {
      color: "#fff",
      borderColor: mTokens.error,
      backgroundColor: mTokens.error,
      boxShadow: "0 4px 14px rgba(239,68,68,0.28)",
      "& .MuiTypography-root": { color: "#fff" },
    },
  };

  const previewIconSx = {
    borderRadius: "8px",
    borderColor: "rgba(0,184,217,0.45)",
    color: "#00B8D9",
    backgroundColor: isDark ? "rgba(0,184,217,0.08)" : "rgba(0,184,217,0.04)",
    "&:hover": {
      borderColor: "#00B8D9",
      backgroundColor: "rgba(0,184,217,0.18)",
    },
  };

  const ENTITY_ITEMS_PER_PAGE = 20;

  useEffect(() => {

  }, [])
  const handleAck = async () => {
    try {
      const notificationData = {
        notificationId: notification.notificationId,
        comment: ""
      }
      const response = await dispatch(acknowledgeNotification(notificationData)).unwrap();
      if (onAcknowledge) onAcknowledge();
      return response;
    } catch (err) {
      return [];
    }
  };

  const handleAssign = async () => {
    setShowAssignPopup(true)
  }

  const handleNavigation = () => {
    if (notification.entity === "ORDER") {
      if (notification.entityId) {
        let searchCriteriaOrder: SearchOrder = {
          orderIds: [notification.entityId],
          practiceIds: [],
          manufacturerIds: [],
          patientFirstName: "",
          patientLastName: "",
          productIds: [],
          providerName: "",
          orderNumber: "",
          invoiceNumber: "",
          serialNumber: "",
          fromDate: "",
          toDate: "",
          status: [],
          invoiceStatus: [],
          currentPage: 1,
          itemsPerPage: ENTITY_ITEMS_PER_PAGE,
          downloadState: false,
          fromServiceDate: '',
          toServiceDate: ''
        };
        navigate("/order_list", { state: { orderListSearchCriteria: searchCriteriaOrder } });
      }
    } else if (notification.entity === "IVR") {
      let searchCriteriaIvr: SearchIVRListRequest = {
        ivrIds: [notification.entityId],
        productId: [],
        practiceId: [],
        productCategory: [],
        patientFirstName: "",
        patientLastName: "",
        verificationStatus: [],
        ivrNumber: '',
        fromDate: "",
        toDate: "",
        currentPage: 1,
        itemsPerPage: ENTITY_ITEMS_PER_PAGE
      };
      navigate("/ivr_list", { state: { ivrListSearchCriteria: searchCriteriaIvr } })
    } else if (notification.entity === "PRACTICE") {
      let searchCriteria: SearchPracticeRequest = {
        practiceId: notification.entityId,
        contactFirstName: '',
        contactLastName: '',
        contactEmail: '',
        contactPhone: '',
        approvalStatus: '',
        agencyId: null,
        userId: null,
        currentPage: 1,
        itemsPerPage: ENTITY_ITEMS_PER_PAGE
      };
      navigate("/practice_list", { state: { practiceListSearchCriteria: searchCriteria } })
    }else if (notification.entity === "INVOICE") {
      let searchCriteriaInvoice: SearchInvoiceInfo = {
        invoiceId: notification.entityId ,
        mfgInvoiceNumber: "",
        currentPage: 1,
        fromDate: '',
        toDate: '',
        itemsPerPage: ENTITY_ITEMS_PER_PAGE,
        invoiceStatus: [],
        agencyIds: [],
        practiceIds: [],
        mfgIds: [],
        isDownload: false,
        excludePurchaseAndBill: false,
        patientIds: [],
      };
      navigate("/invoice_list", { state: { invoiceListSearchCriteria: searchCriteriaInvoice } })
    }else if (notification.entity === "PAYMENT") {
      let searchCriteriaPayment = {
        paymentId: notification.entityId ,
        currentPage: 1,
        itemsPerPage: ENTITY_ITEMS_PER_PAGE,
      };
      return navigate("/payment_list", { state: { paymentListSearchCriteria: searchCriteriaPayment } })
    } else if (notification.entity === "PRACTICE_PRODUCT") {
      let agreementListSearchCriteria = {
        agreementIds: [notification.entityId],
        productIds: [],
        manufacturerIds: [],
        practiceIds: [],
        agreementStatus: [],
        agreementValidity: [],
        currentPage: 1,
        itemsPerPage: ENTITY_ITEMS_PER_PAGE
      }
      return navigate("/practice_product_agreement", { state: { agreementListSearchCriteria: agreementListSearchCriteria } })
    }
  }

  const stripHtml = (html: string) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const handleActionPreview = async (attachment: notificationAttachmentDTO, type: string) => {
    const documentId = attachment.documentId;
    if (documentId) {
      const actionResult = await dispatch(fetchOrderDocumentsByIds([documentId]));
      const documentData = actionResult.payload;

      if (documentData?.content) {
        setPDFContent(documentData.content);
        setShowPDF({
          isShow: true,
          title: documentData.patientName,
          fileName: documentData.documentName,
        });
      } else {
        dispatch(setSingleAlertObj({
          message: "Requested Document has not found under server location!",
          alertType: "info",
        }));
      }
    }
  };

  const handleDeleteAction = async () => {
    const data = {
      notificationId: notification.notificationId,
    }

    {
      setShowOptionsPopup({
        message: "Do you really want to delete this notification?",
        show: true,
        variant: "confirm",
        buttons: [{ name: "Confirm", color: 'primary', onClick: () => deleteNotificationById(data) }, { name: "Cancel", color: 'secondary', onClick: cancelOptionPopup }]
      })
    }

  }

  const cancelOptionPopup = () => {
    setShowOptionsPopup({ message: "", show: false });
  }

  const deleteNotificationById = async (data: deleteNotificationRequest) => {
    cancelOptionPopup();
    const response = await dispatch(deleteNotification(data)).unwrap();
    onAcknowledge();
  }

  const handleItemClick = () => {
    setPopupMode('view');
    setShowAssignPopup(true);
  }

  return (
    <>
      {showAssignPopup &&
        <AssignPopup
          mode={popupMode}
          canAssign={isAssignable}
          isAcknowledged={acknowledged}
          attachments={notification.notificationAttachment ? notification.notificationAttachment : []}
          open={showAssignPopup}
          onClose={() => setShowAssignPopup(false)}
          onSubmitAssign={(data) => onAcknowledge()}
          notification={{
            notificationId: notification.notificationId,
            subject: notification.notificationSub ? notification.notificationSub : '',
            message: stripHtml(notification.notificationMsg ? notification.notificationMsg : ''),
            category: notification.senderId != null ? 'user' : 'system',
            time: notification.auditTimeStamp ? notification.auditTimeStamp : ''
          }}
        />
      }
      {showPDF.isShow &&
        <PDFViewer
          isOpen={showPDF.isShow}
          onClose={() => { setShowPDF({ isShow: false, title: "", fileName: "" }) }}
          title={showPDF.title}
          fileName={showPDF.fileName}
          base64String={documentDataFromCommonApi?.content ? documentDataFromCommonApi?.content : pdfContent}
        />
      }
      {showOptionPopup.show &&
        <OptionsPopup
          open={showOptionPopup.show}
          onClose={() => setShowOptionsPopup({ message: "", show: false })}
          variant={showOptionPopup.variant}
          message={<>{showOptionPopup.message}</>}
          buttons={showOptionPopup.buttons}
          title={showOptionPopup.title}
          obj={showOptionPopup.obj}
        />
      }
      <Box
        px={{ xs: 1.5, sm: 2 }}
        py={1.5}
        sx={{
          borderBottom: isLast ? "none" : `1px solid ${mTokens.border}`,
          transition: "background-color 0.15s ease",
          "&:hover": {
            backgroundColor: isDark ? "rgba(20,184,166,0.06)" : "rgba(15,23,42,0.03)",
          },
        }}
      >
        <Grid container alignItems="center" spacing={{ xs: 1.25, sm: 1.5, md: 1 }}>
          <Grid item xs={12} sm={4} md={3} display="flex" alignItems="center" minWidth={0}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                border: `2px solid ${mTokens.accent}`,
                backgroundColor: acknowledged ? "transparent" : mTokens.accent,
                mr: 1.5,
                flexShrink: 0,
                boxShadow: acknowledged ? "none" : "0 0 0 3px rgba(20,184,166,0.18)",
              }}
            />
            <Typography
              fontWeight={700}
              fontSize="0.86rem"
              noWrap
              sx={{ color: mTokens.textPrimary, minWidth: 0 }}
            >
              {notification.notificationSub}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={5} md={acknowledged ? 5 : 6} minWidth={0}>
            <Typography
              fontSize="0.8rem"
              noWrap={!isSm}
              sx={{ color: mTokens.textSecondary }}
            >
              {stripHtml(notification.notificationMsg ? notification.notificationMsg : '')}
            </Typography>
          </Grid>

          {acknowledged && createdDate && createdTime &&
            <Grid item xs={12} sm={5} md={2} minWidth={0}>
              <Typography
                fontSize="0.75rem"
                noWrap={!isSm}
                sx={{ color: mTokens.textSecondary }}
              >
                Created at : {createdDate} {createdTime}
              </Typography>
            </Grid>
          }

          {acknowledged && ackedByUserName &&
            <Grid item xs={12} sm={5} md={2} minWidth={0}>
              <Typography
                fontSize="0.75rem"
                noWrap={!isSm}
                sx={{ color: mTokens.textSecondary }}
              >
                Ack by : {ackedByUserName}
              </Typography>
            </Grid>
          }

          <Grid
            item
            xs={12}
            sm={12}
            md={acknowledged ? 2 : 3}
            display="flex"
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
            gap={0.75}
            alignItems="center"
            flexWrap="wrap"
            sx={{ ml: { xs: 0, md: "auto" }, rowGap: 0.75 }}
          >
            {notification.notificationAttachment && notification.notificationAttachment.length > 0 &&
              notification.notificationAttachment.map((attachment, index) => (
                <CustomIconButton
                  key={index}
                  color="info"
                  size="small"
                  variant="outlined"
                  onClick={() => handleActionPreview(attachment, "document")}
                  tooltipMessage={`${attachment.documentName}`}
                  tooltipPlacement="bottom"
                  sx={{ ...previewIconSx, ml: index > 0 ? 0.5 : 0 }}
                >
                  <PreviewOutlined fontSize="small" />
                </CustomIconButton>
              ))}
              {!acknowledged &&
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    removeUpperCase
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAck();
                    }}
                    sx={ackBtnSx}
                  >
                    Acknowledge
                  </Button>
                  {isAssignable &&
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      removeUpperCase
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAssign();
                      }}
                      sx={goAssignBtnSx}
                    >
                      Assign
                    </Button>
                  }
                </>
              }
              <Button
                size="small"
                variant="outlined"
                color="info"
                removeUpperCase
                onClick={handleItemClick}
                sx={viewBtnSx}
              >
                View
              </Button>
              {showGoButton &&
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  removeUpperCase
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNavigation();
                  }}
                  sx={goAssignBtnSx}
                >
                  Go
                </Button>
              }
              {acknowledged &&
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  removeUpperCase
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteAction();
                  }}
                  sx={deleteBtnSx}
                >
                  Delete
                </Button>
              }
              <Typography
                fontSize="0.75rem"
                noWrap={!isSm}
                sx={{ color: mTokens.textSecondary, ml: { xs: 0, sm: 0.25 } }}
              >
                {formattedTime || formattedDate}
              </Typography>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default NotificationItem;
